import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { gateway } from "../gateway";
import { nextViewConnectBusinessState, resetConnectBusinessState, updateConnectBusinessState } from "../redux/features/connectBusinessStateSlice";
import { ConnectBusinessExpandNoEmployees } from "./connectBusinessExpandNoEmployees";
import { connectBusinessFlowColors, connectBusinessFlowSx } from "./connectBusinessFlowStyles";
import { LoaderComponent } from "./Loader";
import { colors } from "./ui/colors";
import { orange } from "@mui/material/colors";

export const ConnectBusinessSearch = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tokenLocal = sessionStorage.getItem("token");
  const firstMiles = 50;
  const secondMiles = 80;

  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const [state, setState] = useState("running");
  const [miles, setMiles] = useState(firstMiles);

  const connectBusinessState = useSelector((state) => state.connectBusinessState);
  const { lat, lng } = connectBusinessState;

  const handleNoClick = () => {
    setLoading(true);
    setIsVisible(false);
    setTimeout(() => {
      dispatch(resetConnectBusinessState());
      setLoading(false);
      navigate("/business");
    }, 1000);
  };

  const handleYesClick = () => {
    setOpen(true);
    setState("running");
    setMiles(secondMiles);
  };

  const handleEmployeeNearby = async () => {
    if (connectBusinessState.isDemo) {
      const dummyData = [
        {
          uid: "demo-1",
          firstName: "John",
          lastName: "Doe",
          city: "New York",
          state: "NY",
          country: "USA",
          avatar: "https://i.pravatar.cc/150?u=demo1",
          distances: { driving: { duration: "15 min", distance: "5 miles" } },
        },
        {
          uid: "demo-2",
          firstName: "Jane",
          lastName: "Smith",
          city: "Brooklyn",
          state: "NY",
          country: "USA",
          avatar: "https://i.pravatar.cc/150?u=demo2",
          distances: { driving: { duration: "25 min", distance: "8 miles" } },
        },
        {
          uid: "demo-3",
          firstName: "Mike",
          lastName: "Johnson",
          city: "Queens",
          state: "NY",
          country: "USA",
          avatar: "https://i.pravatar.cc/150?u=demo3",
          distances: { driving: { duration: "35 min", distance: "12 miles" } },
        },
      ];
      setOpen(false);
      dispatch(updateConnectBusinessState({ bestChoices: dummyData }));
      dispatch(nextViewConnectBusinessState());
      return;
    }

    try {
      const response = await gateway.get(`/users/nearby?latitude=${lat}&longitude=${lng}&coverageMiles=${miles}`, {
        headers: { Authorization: `${tokenLocal}` },
      });
      const data = response.data.data;
      setOpen(false);
      if (data.length <= 0) {
        if (miles === secondMiles) {
          setState("noEmployees");
          return;
        }
        setState("error");
        return;
      }
      dispatch(updateConnectBusinessState({ bestChoices: data }));
      dispatch(nextViewConnectBusinessState());
    } catch (error) {
      console.error(error);
      setOpen(false);
      setState("error");
    }
  };

  useEffect(() => {
    handleEmployeeNearby();
  }, [state]);

  return (
    <Box
      key="connectacleaner-search"
      sx={{
        height: "84vh",
        opacity: isVisible ? 1 : 0,
        transition: "opacity 1s ease-in-out",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      {open ? (
        <LoaderComponent open={open} type="spinner" />
      ) : state === "error" ? (
        <Box
          sx={{
            width: "100%",
            maxWidth: 500, // Limitamos el ancho para que no se estire demasiado
            bgcolor: "background.paper",
            borderRadius: "24px", // Bordes más redondeados y modernos
            overflow: "hidden",
            border: `1px solid ${connectBusinessFlowColors.border}`, // Borde casi invisible
          }}
        >
          {/* Header con degradado o color sólido refinado */}
          <Box
            sx={{
              bgcolor: connectBusinessFlowColors.band,
              color: connectBusinessFlowColors.text,
              p: 4,
              textAlign: "center",
              position: "relative",
            }}
          >
            <SentimentVeryDissatisfiedIcon
              sx={{
                fontSize: 60,
                mb: 1,
                opacity: 0.9,
                color: colors.orange.base,
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, letterSpacing: "-0.5px" }}>
              WE ARE SORRY
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
              We couldn’t find any cleaner in your area
            </Typography>
          </Box>

          {/* Cuerpo de la tarjeta */}
          <Stack spacing={3} sx={{ p: 4, alignItems: "center" }}>
            <Typography variant="subtitle1" align="center" sx={{ color: "text.secondary", fontWeight: 500 }}>
              Would you like to expand the search area to find more options?
            </Typography>

            <Box sx={{ display: "flex", gap: 2, width: "100%", justifyContent: "center" }}>
              <Button
                variant="outlined"
                onClick={handleNoClick}
                sx={{
                  ...connectBusinessFlowSx.secondaryButton,
                  px: 4,
                  py: 1.5,
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 700,
                  flex: 1,
                  borderColor: colors.orange.base,
                  color: colors.orange.base,
                  "&: hover": { borderColor: colors.orange.base, bgcolor: colors.orange[50] },
                }}
              >
                No, thanks
              </Button>

              <Button
                variant="contained"
                onClick={handleYesClick}
                sx={{
                  ...connectBusinessFlowSx.primaryButton,
                  bgcolor: colors.orange.base,
                  px: 4,
                  py: 1.5,
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 700,
                  flex: 1,
                  boxShadow: `0 4px 14px 0 ${connectBusinessFlowColors.band}66`,
                  "&: hover": { bgcolor: colors.orange[600] },
                }}
              >
                Yes, expand
              </Button>
            </Box>
          </Stack>
        </Box>
      ) : state === "noEmployees" ? (
        <ConnectBusinessExpandNoEmployees />
      ) : (
        <div />
      )}
    </Box>
  );
};
